import { db, rootRef } from './firebase';

const MIGRATION_FLAG = 'peer_eval_v2';

export interface LegacyScanResult {
  total: number;
  valid: number;
  legacyMissingEvaluator: number;
  legacyWrongDocId: number;
  migrated: number;
  archived: number;
}

function expectedDocId(evaluatorId: string, colleagueId: string, questionId: number | string) {
  return `${evaluatorId}_${colleagueId}_${questionId}`;
}

function expectedCommentDocId(evaluatorId: string, colleagueId: string) {
  return `${evaluatorId}_${colleagueId}`;
}

/**
 * 구버전 다면평가 데이터 정리
 * - evaluatorId 필드 없음: 평가자 특정 불가 -> legacy 아카이브로 이동
 * - evaluatorId 있으나 doc ID 불일치: 신규 doc ID로 복사 후 구 doc 삭제
 */
export async function migrateLegacyPeerEvals(): Promise<LegacyScanResult> {
  const flagRef = rootRef().collection('settings').doc(MIGRATION_FLAG);
  const flagSnap = await flagRef.get();
  if (flagSnap.exists) {
    return {
      total: 0,
      valid: 0,
      legacyMissingEvaluator: 0,
      legacyWrongDocId: 0,
      migrated: 0,
      archived: 0,
    };
  }

  const result: LegacyScanResult = {
    total: 0,
    valid: 0,
    legacyMissingEvaluator: 0,
    legacyWrongDocId: 0,
    migrated: 0,
    archived: 0,
  };

  const evalSnap = await rootRef().collection('peer_evals').get();
  result.total = evalSnap.size;

  let batch = db.batch();
  let batchOps = 0;
  const MAX_BATCH = 400;

  const flush = async () => {
    if (batchOps === 0) return;
    await batch.commit();
    batch = db.batch();
    batchOps = 0;
  };

  for (const doc of evalSnap.docs) {
    const data = doc.data();
    const evaluatorId = data.evaluatorId as string | undefined;
    const colleagueId = data.colleagueId as string | undefined;
    const questionId = data.questionId as number | string | undefined;

    if (!evaluatorId || !colleagueId || questionId == null) {
      result.legacyMissingEvaluator += 1;
      batch.set(rootRef().collection('peer_evals_legacy').doc(doc.id), {
        ...data,
        _archivedAt: Date.now(),
        _archiveReason: 'missing_evaluator_or_required_fields',
        _originalDocId: doc.id,
      });
      batch.delete(doc.ref);
      batchOps += 2;
      result.archived += 1;
    } else {
      const newId = expectedDocId(evaluatorId, colleagueId, questionId);
      if (doc.id === newId) {
        result.valid += 1;
      } else {
        result.legacyWrongDocId += 1;
        batch.set(
          rootRef().collection('peer_evals').doc(newId),
          { ...data, migratedFrom: doc.id, updatedAt: Date.now() },
          { merge: true }
        );
        batch.delete(doc.ref);
        batchOps += 2;
        result.migrated += 1;
      }
    }

    if (batchOps >= MAX_BATCH) await flush();
  }

  const commentSnap = await rootRef().collection('peer_comments').get();
  for (const doc of commentSnap.docs) {
    const data = doc.data();
    const evaluatorId = data.evaluatorId as string | undefined;
    const colleagueId = data.colleagueId as string | undefined;

    if (!evaluatorId || !colleagueId) {
      batch.set(rootRef().collection('peer_comments_legacy').doc(doc.id), {
        ...data,
        _archivedAt: Date.now(),
        _archiveReason: 'missing_evaluator_or_colleague',
        _originalDocId: doc.id,
      });
      batch.delete(doc.ref);
      batchOps += 2;
      result.archived += 1;
    } else {
      const newId = expectedCommentDocId(evaluatorId, colleagueId);
      if (doc.id !== newId) {
        batch.set(
          rootRef().collection('peer_comments').doc(newId),
          { ...data, migratedFrom: doc.id, updatedAt: Date.now() },
          { merge: true }
        );
        batch.delete(doc.ref);
        batchOps += 2;
        result.migrated += 1;
      }
    }

    if (batchOps >= MAX_BATCH) await flush();
  }

  await flush();

  await flagRef.set({
    done: true,
    at: Date.now(),
    ...result,
  });

  if (result.legacyMissingEvaluator > 0 || result.legacyWrongDocId > 0 || result.migrated > 0) {
    console.info('[MIGRATE] peer_eval legacy cleanup:', result);
  }

  return result;
}

export function isValidPeerAnswer(data: {
  evaluatorId?: string;
  colleagueId?: string;
  questionId?: unknown;
}) {
  return Boolean(data.evaluatorId && data.colleagueId && data.questionId != null);
}

export function isValidPeerComment(data: { evaluatorId?: string; colleagueId?: string }) {
  return Boolean(data.evaluatorId && data.colleagueId);
}
